
























import { cloneDeep } from 'lodash'
import axios from '@/helpers/axios'
import { Component, Vue, Watch } from 'vue-property-decorator'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { faSave } from '@fortawesome/free-regular-svg-icons'
import addFontAwesomeIcon from '@/helpers/add-font-awesome-icon'

addFontAwesomeIcon(faSave)
addFontAwesomeIcon(faCheck)

@Component
export default class SaveButton extends Vue {
	protected saving = false
	protected progress = 0
	protected uploadPercentage: Record<string, number> = {}
	protected step = ''
	protected successful = false
	protected formDatas: {
		oldOriginals: FormData
		newOriginals: Record<string, FormData>
		platforms: Record<string, FormData>
	} = { newOriginals: {}, oldOriginals: new FormData(), platforms: {} }

	get images() {
		return this.$store.state.images.images
	}

	/**
	 * Visually start the saving process, but really decouple it by using a
	 * 50ms timeout, so that it doesn't lag so much.
	 */
	save() {
		if (this.saving) {
			this.$store.dispatch('notifications/ADD', {
				message: this.$t(
					'Please wait for the saving process to be done before attempting a new save'
				),
				type: 'warning',
				scrollUp: true
			})

			return
		}

		this.progress = 0
		this.uploadPercentage = {}
		this.saving = true

		this.gatherData()
			.then(() => (this.step = 'Uploading images'))
			.then(() => this.sendDataToServer())
			.then(() => (this.progress = 100))
			.then(() => (this.successful = true))
			.then(() =>
				this.$store.dispatch('notifications/ADD', {
					type: 'success',
					message: this.$t('Successfully saved product')
				})
			)
			.then(() => setTimeout(() => (this.successful = false), 5000))
			.catch(this.$handleError)
			.finally(() => (this.saving = false))
	}

	gatherData() {
		this.formDatas = {
			newOriginals: {},
			oldOriginals: new FormData(),
			platforms: {}
		}

		const requiredPromises = this.attachOriginals()
		const optionalPromises = this.attachPlatformImages()

		this.attachPlatformData()

		return Promise.all([
			Promise.all(requiredPromises),
			Promise.allSettled(optionalPromises)
		])
	}

	async sendDataToServer() {
		const platforms = Object.entries(this.formDatas.platforms)

		// Save platforms first to not clear out originals unnecessarily.
		await Promise.all([
			...platforms.map(([id, platform]) => this.savePart(platform, id)),
			...(await this.saveOriginals())
		])
	}

	async saveOriginals() {
		if ([...this.formDatas.oldOriginals.keys()].length) {
			await this.savePart(this.formDatas.oldOriginals, `old_originals`)
		} else {
			const clearFormData = new FormData()

			clearFormData.set(`originals[clear_1][clear]`, '1')
			clearFormData.set(`originals[clear_2][clear]`, '1')

			await this.savePart(clearFormData, `old_originals`)
		}

		// First save the old originals to clear all other files, then (in
		// parallel) save all new originals
		return Object.entries(
			this.formDatas.newOriginals
		).map(([name, formData]) =>
			this.savePart(formData, `original[${name}]`)
		)
	}

	savePart(formData: FormData, progressKey: string) {
		return axios.post(`${this.$store.getters.product.sku}/save`, formData, {
			onUploadProgress: e => this.uploadProgress(e, progressKey)
		})
	}

	uploadProgress(ev, progressKey: string) {
		if (ev.lengthComputable && this.progress < 80) {
			this.uploadPercentage[progressKey] = Math.min(
				1,
				ev.loaded / ev.total
			)

			const uploadPercentageEntries = Object.entries(
				this.uploadPercentage
			)

			const uploadPercentageSum = uploadPercentageEntries.reduce(
				(a, b) => a + b[1],
				0
			)

			this.progress = Math.round(
				80 *
					(uploadPercentageSum /
						(100 * uploadPercentageEntries.length))
			)
		}

		if (this.progress >= 80) {
			const increase = () => {
				if (this.progress && this.progress < 99) {
					this.progress++

					setTimeout(increase, 100)
				}
			}

			setTimeout(increase, 100)
		}
	}

	@Watch('progress', { immediate: true })
	selectStep(n) {
		switch (n) {
			case 0:
				this.step = 'Generating images'
				break

			case 80:
				this.step = 'Processing images on server'
				break

			case 99:
				this.step = 'Copying images to final destination'
				break

			case 100:
				this.step = 'Done'
				break
		}
	}

	attachPlatformData() {
		this.$store.getters['platforms/data'].forEach(platform => {
			if (!this.formDatas.platforms[platform.id]) {
				this.formDatas.platforms[platform.id] = new FormData()
			}

			this.formDatas.platforms[platform.id].set(
				`platforms[${platform.id}][title]`,
				platform.title
			)

			this.formDatas.platforms[platform.id].set(
				`platforms[${platform.id}][active]`,
				platform.active
			)

			this.formDatas.platforms[platform.id].set(
				`platforms[${platform.id}][custom]`,
				platform.custom
			)
		})
	}

	attachOriginals() {
		// Preset images belong to the measurement preset, not the product
		return this.images
			.filter(i => i.category !== 'permanent' && !i.isPreset)
			.map(this.attachOriginal)
	}

	async attachOldOriginal(i, imageData) {
		this.formDatas.oldOriginals.set(`originals[${i.id}][data]`, imageData)
		this.formDatas.oldOriginals.set(
			`originals[${i.id}][source]`,
			i.image.src
		)
	}

	async attachNewOriginal(i, imageData) {
		const formData = new FormData()

		formData.set(`originals[${i.id}][data]`, imageData)
		formData.set(`originals[${i.id}][image]`, await i.image.blob)

		this.formDatas.newOriginals[i.id] = formData
	}

	attachOriginal(i) {
		const imageData = this.getOriginalData(i)

		if (i.image.isLink) {
			return this.attachOldOriginal(i, imageData)
		}

		return this.attachNewOriginal(i, imageData)
	}

	getOriginalData(i) {
		return JSON.stringify({
			objects: i.objects,
			categories: i.categories,
			categorizedManually: !!i.categorizedManually,
			order: i.order,
			dimensions: {
				width: i.dimensions.width,
				height: i.dimensions.height
			}
		})
	}

	attachPlatformImages() {
		const platforms = this.$store.state.platforms.platforms

		return Object.values(platforms).flatMap(platform =>
			this.getPlatformImages(platform)
		)
	}

	getPlatformImages(platform: PlatformType) {
		return platform.images.map((image, ix) =>
			this.getFinalPlatformImage(image, ix, platform)
		)
	}

	getFinalPlatformImage(
		imageObject: PlatformImage,
		ix: number,
		platform: PlatformType
	) {
		if (!imageObject || !imageObject.image) {
			// eslint-disable-next-line no-console
			console.error(
				`An unknown error occurred with image ${ix} for ${platform.settings.name}`
			)

			return Promise.reject('Unknown error')
		}

		const {
			image: { id, isPreset, presetImagePath }
		} = imageObject

		// The rule can be shared with the platform settings, so never modify it
		const rule = cloneDeep(imageObject.rule)

		const formDataPrefix = `platforms[${platform.id}][images][${ix}]`

		return this.$store
			.dispatch('images/GET_IMAGE_COMPOSITION_FOR_SAVING', {
				rule,
				id,
				dimensions: platform.settings.format,
				globalPadding: this.$settings.platforms.padding,
				useBoundingBoxForGeneration: this.$settings.gallery
					.useBoundingBoxForGeneration,
				boundingBoxPadding: this.$settings.gallery.boundingBoxPadding
			})
			.then(({ image, key }) => {
				this.formDatas.platforms[platform.id].set(
					`${formDataPrefix}[image]`,
					key
				)

				if (image.isLink) {
					return Promise.resolve(
						this.formDatas.platforms[platform.id].set(
							`images[${key}]`,
							image.src
						)
					)
				}

				// If the image data has already been set for this specific key, do not
				// request a new blob. URLs to images, however are always preferred, so
				// we allow them to overwrite the old data.
				if (
					this.formDatas.platforms[platform.id].has(`images[${key}]`)
				) {
					return Promise.resolve()
				}

				return image.blob.then(blob =>
					this.formDatas.platforms[platform.id].set(
						`images[${key}]`,
						blob
					)
				)
			})
			.then(() => {
				rule.version = process.env.VUE_APP_VERSION

				this.formDatas.platforms[platform.id].set(
					`${formDataPrefix}[rule]`,
					JSON.stringify(rule)
				)
				this.formDatas.platforms[platform.id].set(
					`${formDataPrefix}[original]`,
					id
				)

				if (isPreset) {
					this.formDatas.platforms[platform.id].set(
						`${formDataPrefix}[preset_image]`,
						presetImagePath
					)
				}
			})
			.catch(e => {
				this.$handleError(e)

				this.formDatas.platforms[platform.id].delete(
					`${formDataPrefix}[image]`
				)
				this.formDatas.platforms[platform.id].delete(
					`${formDataPrefix}[rule]`
				)
				this.formDatas.platforms[platform.id].delete(
					`${formDataPrefix}[original]`
				)
				this.formDatas.platforms[platform.id].delete(
					`${formDataPrefix}[order]`
				)
				this.formDatas.platforms[platform.id].delete(
					`${formDataPrefix}[preset_image]`
				)
			})
	}
}
